import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  Text,
  Badge,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  VStack,
  SimpleGrid,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useColorModeValue,
  Select
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBrain,
  faChartBar,
  faChartLine,
  faCheck,
  faTimes,
  faEye,
  faSearch,
  faChevronDown,
  faChevronRight,
  faChevronLeft,
  faBookOpen,
  faLayerGroup,
  faPoll
} from '@fortawesome/free-solid-svg-icons';
import {
  StudentQuizAttempt,
  QuestionDifficultyItem,
  computeQuestionDifficulty
} from '@/functions/analyticsService';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip
} from 'recharts';

// Custom Recharts Tooltip for Question Diagnostics
const CustomChartTooltip: React.FC<any> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isHigh = data.errorRate >= 70;
    const isMed = data.errorRate >= 40 && data.errorRate < 70;
    const badgeColor = isHigh ? '#f43f5e' : isMed ? '#0ea5e9' : '#10b981';

    return (
      <Box
        bg="rgba(15, 23, 42, 0.95)"
        backdropFilter="blur(10px)"
        border="1px solid rgba(255, 255, 255, 0.15)"
        p={3}
        borderRadius="xl"
        shadow="2xl"
        fontSize="xs"
        maxW="280px"
        zIndex={100}
      >
        <Flex justify="space-between" align="center" mb={1} gap={2}>
          <HStack spacing={1.5}>
            <Badge bg="rgba(14, 165, 233, 0.2)" color="#38bdf8" fontSize="3xs" px={1.5} py={0.5} borderRadius="md">
              {data.label}
            </Badge>
            {data.chapterName && (
              <Badge variant="outline" colorScheme="gray" fontSize="3xs" maxW="110px" isTruncated>
                {data.chapterName}
              </Badge>
            )}
          </HStack>
          <Badge
            bg={isHigh ? 'rgba(244, 63, 94, 0.2)' : isMed ? 'rgba(14, 165, 233, 0.2)' : 'rgba(16, 185, 129, 0.2)'}
            color={badgeColor}
            fontSize="3xs"
            fontWeight="800"
            borderRadius="md"
            px={1.5}
          >
            {data.errorRate}% ERROR
          </Badge>
        </Flex>

        <Text color="gray.200" fontSize="xs" fontWeight="700" noOfLines={2} my={1}>
          {data.questionText}
        </Text>

        <Divider borderColor="whiteAlpha.200" my={1.5} />

        <SimpleGrid columns={2} spacing={2} fontSize="3xs">
          <Box>
            <Text color="gray.400">Class Accuracy</Text>
            <Text fontWeight="800" color="#10b981" fontSize="2xs">
              {data.accuracyRate}%
            </Text>
          </Box>
          <Box>
            <Text color="gray.400">Missed Answers</Text>
            <Text fontWeight="800" color={isHigh ? '#f43f5e' : '#38bdf8'} fontSize="2xs">
              {data.wrongAttempts} of {data.totalAttempts} students
            </Text>
          </Box>
        </SimpleGrid>

        <Text fontSize="4xs" color="cyan.400" mt={2} textAlign="center" fontWeight="600">
          💡 Click to inspect full choices &amp; misconceptions
        </Text>
      </Box>
    );
  }
  return null;
};

interface QuestionDifficultyDeckProps {
  attempts: StudentQuizAttempt[];
  courseId: string;
  courseName: string;
  isGlobal?: boolean;
  courseSections?: any[];
}

export const QuestionDifficultyDeck: React.FC<QuestionDifficultyDeckProps> = ({
  attempts,
  courseId: _courseId,
  courseName: _courseName,
  isGlobal: _isGlobal = false,
  courseSections = []
}) => {
  // Tabs: 'diagnostics' (visual chart + compact table) | 'surveys' (survey & poll distributions) | 'roster' (full question review)
  const [activeTab, setActiveTab] = useState<'diagnostics' | 'surveys' | 'roster'>('diagnostics');
  const [chartStyle, setChartStyle] = useState<'spline' | 'capsule'>('spline');
  const [selectedQuizScope, setSelectedQuizScope] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [errorThreshold, setErrorThreshold] = useState<'all' | 'high' | 'med' | 'low'>('all');
  const [sortBy, setSortBy] = useState<'errorDesc' | 'errorAsc' | 'wrongDesc' | 'index'>('errorDesc');
  const [page, setPage] = useState<number>(1);
  const pageSize = 6; // Compact page size to completely prevent vertical scrolling

  const [inspectQuestion, setInspectQuestion] = useState<QuestionDifficultyItem | null>(null);
  const { isOpen: isInspectOpen, onOpen: onInspectOpen, onClose: onInspectClose } = useDisclosure();

  // Colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const rowHoverBg = useColorModeValue('rgba(14, 165, 233, 0.05)', 'rgba(14, 165, 233, 0.1)');

  // 1. Discover all quizzes (Course Quiz + Section Quizzes)
  const availableQuizzes = useMemo(() => {
    const map: Record<string, {
      quizId: string;
      title: string;
      chapterName: string;
      isSectionQuiz: boolean;
      attemptCount: number;
    }> = {};

    function traverse(sections: any[], parentTitle = '') {
      sections.forEach(s => {
        const currentChapter = parentTitle ? `${parentTitle} › ${s.title}` : s.title;
        if (s.contents && Array.isArray(s.contents)) {
          s.contents.forEach((item: any) => {
            if (item.type === 'quiz') {
              map[item.id] = {
                quizId: item.id,
                title: item.title || 'Section Quiz',
                chapterName: currentChapter,
                isSectionQuiz: true,
                attemptCount: 0
              };
            }
          });
          const subSections = s.contents.filter((item: any) => item.type === 'section');
          if (subSections.length > 0) {
            traverse(subSections, currentChapter);
          }
        }
      });
    }

    if (courseSections && courseSections.length > 0) {
      traverse(courseSections);
    }

    attempts.forEach(a => {
      const qId = a.quizId || `${a.courseId}-quiz`;
      const isSection = a.chapterName &&
        a.chapterName !== 'General' &&
        a.chapterName !== 'General Examination' &&
        a.chapterName !== 'General Assessment' &&
        !qId.endsWith('-quiz');

      if (!map[qId]) {
        map[qId] = {
          quizId: qId,
          title: a.quizTitle || (isSection ? `${a.chapterName} Quiz` : 'Course Assessment Quiz'),
          chapterName: a.chapterName || (isSection ? a.chapterName : 'General Assessment'),
          isSectionQuiz: Boolean(isSection),
          attemptCount: 0
        };
      }
      map[qId].attemptCount += 1;
    });

    return Object.values(map);
  }, [courseSections, attempts]);

  const courseQuizzes = useMemo(() => availableQuizzes.filter(q => !q.isSectionQuiz), [availableQuizzes]);
  const sectionQuizzes = useMemo(() => availableQuizzes.filter(q => q.isSectionQuiz), [availableQuizzes]);

  // Selected quiz metadata label
  const selectedScopeLabel = useMemo(() => {
    if (selectedQuizScope === 'all') {
      return `All Quizzes & Sections (${attempts.length} att)`;
    }
    const found = availableQuizzes.find(q => q.quizId === selectedQuizScope);
    if (found) {
      return found.isSectionQuiz
        ? `[Section] ${found.chapterName} - ${found.title}`
        : `[Course] ${found.title}`;
    }
    return 'Selected Quiz';
  }, [selectedQuizScope, availableQuizzes, attempts]);

  // 2. Compute question difficulty list for selected scope
  const allQuestions = useMemo(() => {
    return computeQuestionDifficulty(attempts, selectedQuizScope);
  }, [attempts, selectedQuizScope]);

  // Split into standard quiz questions and survey poll questions
  const surveyQuestions = useMemo(() => {
    return allQuestions.filter(q => q.isSurvey || (q.pollDistribution && q.pollDistribution.length > 0 && !q.correctAnswer));
  }, [allQuestions]);

  const gradedQuestions = useMemo(() => {
    return allQuestions.filter(q => !q.isSurvey);
  }, [allQuestions]);

  // Filtered & Sorted for Display
  const filteredQuestions = useMemo(() => {
    let list = activeTab === 'surveys' ? [...surveyQuestions] : [...gradedQuestions];

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(item =>
        (item.questionText && item.questionText.toLowerCase().includes(q)) ||
        (item.chapterName && item.chapterName.toLowerCase().includes(q)) ||
        (item.correctAnswer && item.correctAnswer.toLowerCase().includes(q))
      );
    }

    // Threshold filter
    if (errorThreshold === 'high') {
      list = list.filter(item => item.errorRate >= 70);
    } else if (errorThreshold === 'med') {
      list = list.filter(item => item.errorRate >= 40 && item.errorRate < 70);
    } else if (errorThreshold === 'low') {
      list = list.filter(item => item.errorRate < 40);
    }

    // Sorting
    list.sort((a, b) => {
      if (sortBy === 'errorDesc') return b.errorRate - a.errorRate || b.wrongAttempts - a.wrongAttempts;
      if (sortBy === 'errorAsc') return a.errorRate - b.errorRate || a.wrongAttempts - b.wrongAttempts;
      if (sortBy === 'wrongDesc') return b.wrongAttempts - a.wrongAttempts || b.errorRate - a.errorRate;
      return 0;
    });

    return list;
  }, [activeTab, gradedQuestions, surveyQuestions, searchQuery, errorThreshold, sortBy]);

  // Pagination to completely prevent page stretching
  const totalPages = Math.ceil(filteredQuestions.length / pageSize) || 1;
  const paginatedQuestions = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredQuestions.slice(start, start + pageSize);
  }, [filteredQuestions, page, pageSize]);

  // Summary Metrics
  const summaryMetrics = useMemo(() => {
    const totalQ = gradedQuestions.length;
    if (totalQ === 0) {
      return {
        totalQuestions: 0,
        avgErrorRate: 0,
        criticalCount: 0,
        surveysCount: surveyQuestions.length
      };
    }
    const avgErr = Math.round(gradedQuestions.reduce((sum, q) => sum + q.errorRate, 0) / totalQ);
    const critical = gradedQuestions.filter(q => q.errorRate >= 70).length;

    return {
      totalQuestions: totalQ,
      avgErrorRate: avgErr,
      criticalCount: critical,
      surveysCount: surveyQuestions.length
    };
  }, [gradedQuestions, surveyQuestions]);

  const handleInspect = (q: QuestionDifficultyItem) => {
    setInspectQuestion(q);
    onInspectOpen();
  };

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm">
      <CardBody p={5}>
        {/* Header & Controls Bar */}
        <Flex justify="space-between" align={{ base: 'start', md: 'center' }} mb={3.5} flexWrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Box
              p={2.5}
              bg="linear-gradient(135deg, rgba(14, 165, 233, 0.15) 0%, rgba(16, 185, 129, 0.15) 100%)"
              color="#0284c7"
              borderRadius="xl"
            >
              <FontAwesomeIcon icon={faBrain} />
            </Box>
            <Box>
              <HStack spacing={2} align="center">
                <Heading size="sm" fontWeight="800">
                  Assessment Diagnostics & Poll Insights
                </Heading>
                <Badge
                  bg="#0B192C"
                  color="#38bdf8"
                  border="1px solid rgba(14, 165, 233, 0.3)"
                  borderRadius="full"
                  px={2}
                  py={0.5}
                  fontSize="3xs"
                >
                  Diagnostic & Polls
                </Badge>
              </HStack>
              <Text fontSize="xs" color={mutedText} mt={0.5}>
                Question mastery diagnostics, error rate distributions, and survey poll response breakdown
              </Text>
            </Box>
          </HStack>

          {/* Right Controls: Quiz/Section Scope Selector & Tab Toggles */}
          <HStack spacing={2} flexWrap="wrap">
            {/* Quiz Scope Selector (Course Quiz vs Section Quizzes) */}
            <Menu placement="bottom-end">
              <MenuButton
                as={Button}
                size="xs"
                variant="outline"
                borderColor={borderColor}
                borderRadius="lg"
                fontSize="xs"
                fontWeight="700"
                leftIcon={<FontAwesomeIcon icon={faLayerGroup} color="#0ea5e9" />}
                rightIcon={<FontAwesomeIcon icon={faChevronDown} fontSize="9px" />}
                maxW="250px"
                isTruncated
              >
                {selectedScopeLabel}
              </MenuButton>
              <MenuList zIndex={20} shadow="xl" borderRadius="xl" maxH="320px" overflowY="auto" fontSize="xs">
                <MenuItem
                  fontWeight={selectedQuizScope === 'all' ? '800' : 'normal'}
                  color={selectedQuizScope === 'all' ? 'cyan.400' : undefined}
                  onClick={() => { setSelectedQuizScope('all'); setPage(1); }}
                >
                  🌐 All Quizzes & Sections ({attempts.length} attempts)
                </MenuItem>

                {courseQuizzes.length > 0 && (
                  <>
                    <MenuDivider />
                    <Box px={3} py={1} fontSize="3xs" fontWeight="800" textTransform="uppercase" color={mutedText}>
                      Overall Course Quiz
                    </Box>
                    {courseQuizzes.map(q => (
                      <MenuItem
                        key={q.quizId}
                        fontWeight={selectedQuizScope === q.quizId ? '800' : 'normal'}
                        color={selectedQuizScope === q.quizId ? 'cyan.400' : undefined}
                        onClick={() => { setSelectedQuizScope(q.quizId); setPage(1); }}
                      >
                        📘 {q.title} ({q.attemptCount} att)
                      </MenuItem>
                    ))}
                  </>
                )}

                {sectionQuizzes.length > 0 && (
                  <>
                    <MenuDivider />
                    <Box px={3} py={1} fontSize="3xs" fontWeight="800" textTransform="uppercase" color={mutedText}>
                      Section-Specific Quizzes ({sectionQuizzes.length})
                    </Box>
                    {sectionQuizzes.map(q => (
                      <MenuItem
                        key={q.quizId}
                        fontWeight={selectedQuizScope === q.quizId ? '800' : 'normal'}
                        color={selectedQuizScope === q.quizId ? 'teal.400' : undefined}
                        onClick={() => { setSelectedQuizScope(q.quizId); setPage(1); }}
                      >
                        📑 [{q.chapterName}] {q.title} {q.attemptCount > 0 ? `(${q.attemptCount} att)` : '(0 att)'}
                      </MenuItem>
                    ))}
                  </>
                )}
              </MenuList>
            </Menu>

            {/* View Mode Toggle: Diagnostics vs Survey Polls vs Roster */}
            <HStack bg={useColorModeValue('gray.100', 'whiteAlpha.100')} p={0.5} borderRadius="lg" spacing={1}>
              <Button
                size="xs"
                variant={activeTab === 'diagnostics' ? 'solid' : 'ghost'}
                colorScheme={activeTab === 'diagnostics' ? 'cyan' : 'gray'}
                borderRadius="md"
                leftIcon={<FontAwesomeIcon icon={faChartBar} />}
                onClick={() => { setActiveTab('diagnostics'); setPage(1); }}
                fontSize="2xs"
              >
                Diagnostics Graph
              </Button>
              <Button
                size="xs"
                variant={activeTab === 'surveys' ? 'solid' : 'ghost'}
                colorScheme={activeTab === 'surveys' ? 'cyan' : 'gray'}
                borderRadius="md"
                leftIcon={<FontAwesomeIcon icon={faPoll} />}
                onClick={() => { setActiveTab('surveys'); setPage(1); }}
                fontSize="2xs"
              >
                Survey Polls ({summaryMetrics.surveysCount})
              </Button>
              <Button
                size="xs"
                variant={activeTab === 'roster' ? 'solid' : 'ghost'}
                colorScheme={activeTab === 'roster' ? 'cyan' : 'gray'}
                borderRadius="md"
                leftIcon={<FontAwesomeIcon icon={faBookOpen} />}
                onClick={() => { setActiveTab('roster'); setPage(1); }}
                fontSize="2xs"
              >
                Answers & Review
              </Button>
            </HStack>
          </HStack>
        </Flex>

        {/* 4 Compact Stat Pills */}
        <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={2.5} mb={3.5}>
          <Box p={2.5} borderRadius="xl" bg={useColorModeValue('gray.50', 'whiteAlpha.50')} border="1px solid" borderColor={borderColor}>
            <Text fontSize="3xs" fontWeight="700" color={mutedText} textTransform="uppercase">
              Questions
            </Text>
            <Heading size="xs" fontWeight="800" mt={0.5}>
              {summaryMetrics.totalQuestions} Questions
            </Heading>
          </Box>

          <Box p={2.5} borderRadius="xl" bg={useColorModeValue('gray.50', 'whiteAlpha.50')} border="1px solid" borderColor={borderColor}>
            <Text fontSize="3xs" fontWeight="700" color={mutedText} textTransform="uppercase">
              Class Avg Error
            </Text>
            <Heading size="xs" fontWeight="800" mt={0.5} color={summaryMetrics.avgErrorRate >= 50 ? 'orange.400' : '#10b981'}>
              {summaryMetrics.avgErrorRate}%
            </Heading>
          </Box>

          <Box p={2.5} borderRadius="xl" bg={useColorModeValue('gray.50', 'whiteAlpha.50')} border="1px solid" borderColor={borderColor}>
            <Text fontSize="3xs" fontWeight="700" color={mutedText} textTransform="uppercase">
              Critical (&ge; 70% Error)
            </Text>
            <Heading size="xs" fontWeight="800" mt={0.5} color={summaryMetrics.criticalCount > 0 ? 'red.400' : '#10b981'}>
              {summaryMetrics.criticalCount} Questions
            </Heading>
          </Box>

          <Box p={2.5} borderRadius="xl" bg={useColorModeValue('gray.50', 'whiteAlpha.50')} border="1px solid" borderColor={borderColor}>
            <Text fontSize="3xs" fontWeight="700" color={mutedText} textTransform="uppercase">
              Survey Polls
            </Text>
            <Heading size="xs" fontWeight="800" mt={0.5} color="cyan.400">
              {summaryMetrics.surveysCount} Polls Active
            </Heading>
          </Box>
        </SimpleGrid>

        {/* COMPACT EXECUTIVE OVERVIEW CHART (Smooth Spline Curve or Sleek Capsule Equalizer) */}
        {activeTab === 'diagnostics' && gradedQuestions.length > 0 && (() => {
          const chartData = gradedQuestions.map((q, idx) => ({
            index: idx + 1,
            label: `Q${idx + 1}`,
            questionId: q.questionId,
            questionText: q.questionText,
            chapterName: q.chapterName,
            errorRate: q.errorRate,
            accuracyRate: 100 - q.errorRate,
            wrongAttempts: q.wrongAttempts,
            totalAttempts: q.totalAttempts,
            item: q
          }));

          return (
            <Box
              mb={3.5}
              p={3}
              borderRadius="xl"
              bg={useColorModeValue('gray.50', 'whiteAlpha.50')}
              border="1px solid"
              borderColor={borderColor}
            >
              <Flex justify="space-between" align="center" mb={1.5}>
                <HStack spacing={2}>
                  <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={mutedText}>
                    Question Diagnostics Wave &amp; Error Rate Curve
                  </Text>
                  <Badge colorScheme="cyan" variant="subtle" fontSize="4xs" px={1.5} borderRadius="full">
                    {gradedQuestions.length} Questions Plotted
                  </Badge>
                </HStack>

                <HStack spacing={1}>
                  <Button
                    size="xs"
                    variant={chartStyle === 'spline' ? 'solid' : 'ghost'}
                    colorScheme={chartStyle === 'spline' ? 'cyan' : 'gray'}
                    borderRadius="md"
                    leftIcon={<FontAwesomeIcon icon={faChartLine} />}
                    onClick={() => setChartStyle('spline')}
                    fontSize="3xs"
                    px={2}
                    h="20px"
                  >
                    Smooth Wave
                  </Button>
                  <Button
                    size="xs"
                    variant={chartStyle === 'capsule' ? 'solid' : 'ghost'}
                    colorScheme={chartStyle === 'capsule' ? 'cyan' : 'gray'}
                    borderRadius="md"
                    leftIcon={<FontAwesomeIcon icon={faChartBar} />}
                    onClick={() => setChartStyle('capsule')}
                    fontSize="3xs"
                    px={2}
                    h="20px"
                  >
                    Capsule Pillars
                  </Button>
                </HStack>
              </Flex>

              {/* Recharts Responsive Container (Never stretches bars into bricks) */}
              <Box w="100%" h="155px" py={1}>
                <ResponsiveContainer width="100%" height="100%">
                  {chartStyle === 'spline' ? (
                    <AreaChart
                      data={chartData}
                      margin={{ top: 12, right: 18, left: -22, bottom: 0 }}
                      onClick={(e: any) => {
                        const item = e?.activePayload?.[0]?.payload?.item;
                        if (item) handleInspect(item);
                      }}
                    >
                      <defs>
                        <linearGradient id="diffWaveArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.38} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                        </linearGradient>
                        <linearGradient id="diffWaveStroke" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#f43f5e" />
                          <stop offset="60%" stopColor="#0ea5e9" />
                          <stop offset="100%" stopColor="#10b981" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.12)" vertical={false} />
                      <XAxis
                        dataKey="label"
                        stroke="#64748b"
                        fontSize={9}
                        fontWeight={700}
                        tickLine={false}
                        axisLine={{ stroke: 'rgba(148, 163, 184, 0.2)' }}
                      />
                      <YAxis
                        stroke="#64748b"
                        fontSize={9}
                        domain={[0, 100]}
                        ticks={[0, 50, 100]}
                        tickFormatter={(v) => `${v}%`}
                        tickLine={false}
                        axisLine={{ stroke: 'rgba(148, 163, 184, 0.2)' }}
                      />
                      <RechartsTooltip content={<CustomChartTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="errorRate"
                        stroke="url(#diffWaveStroke)"
                        strokeWidth={2.5}
                        fillOpacity={1}
                        fill="url(#diffWaveArea)"
                        dot={{ r: 3.5, fill: '#0ea5e9', stroke: '#0f172a', strokeWidth: 1.5 }}
                        activeDot={{
                          r: 6,
                          fill: '#38bdf8',
                          stroke: '#ffffff',
                          strokeWidth: 2,
                          cursor: 'pointer'
                        }}
                      />
                    </AreaChart>
                  ) : (
                    <BarChart
                      data={chartData}
                      margin={{ top: 12, right: 18, left: -22, bottom: 0 }}
                      onClick={(e: any) => {
                        const item = e?.activePayload?.[0]?.payload?.item;
                        if (item) handleInspect(item);
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.12)" vertical={false} />
                      <XAxis
                        dataKey="label"
                        stroke="#64748b"
                        fontSize={9}
                        fontWeight={700}
                        tickLine={false}
                        axisLine={{ stroke: 'rgba(148, 163, 184, 0.2)' }}
                      />
                      <YAxis
                        stroke="#64748b"
                        fontSize={9}
                        domain={[0, 100]}
                        ticks={[0, 50, 100]}
                        tickFormatter={(v) => `${v}%`}
                        tickLine={false}
                        axisLine={{ stroke: 'rgba(148, 163, 184, 0.2)' }}
                      />
                      <RechartsTooltip content={<CustomChartTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.04)' }} />
                      <Bar
                        dataKey="errorRate"
                        maxBarSize={16}
                        radius={[6, 6, 0, 0]}
                        cursor="pointer"
                      >
                        {chartData.map((entry, index) => {
                          const fill = entry.errorRate >= 70 ? '#f43f5e' : entry.errorRate >= 40 ? '#0ea5e9' : '#10b981';
                          return <Cell key={`bar-cell-${index}`} fill={fill} />;
                        })}
                      </Bar>
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </Box>
            </Box>
          );
        })()}

        {/* Filtering & Sorting Controls Toolbar */}
        <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
          {/* Quick Threshold Buttons */}
          <HStack spacing={1.5} flexWrap="wrap">
            <Button
              size="xs"
              variant={errorThreshold === 'all' ? 'solid' : 'outline'}
              colorScheme="cyan"
              borderRadius="md"
              fontSize="3xs"
              onClick={() => { setErrorThreshold('all'); setPage(1); }}
            >
              All ({activeTab === 'surveys' ? surveyQuestions.length : gradedQuestions.length})
            </Button>
            {activeTab !== 'surveys' && (
              <>
                <Button
                  size="xs"
                  variant={errorThreshold === 'high' ? 'solid' : 'outline'}
                  colorScheme="red"
                  borderRadius="md"
                  fontSize="3xs"
                  onClick={() => { setErrorThreshold('high'); setPage(1); }}
                >
                  🔴 &ge; 70% Error
                </Button>
                <Button
                  size="xs"
                  variant={errorThreshold === 'med' ? 'solid' : 'outline'}
                  colorScheme="orange"
                  borderRadius="md"
                  fontSize="3xs"
                  onClick={() => { setErrorThreshold('med'); setPage(1); }}
                >
                  🟠 40% - 69%
                </Button>
                <Button
                  size="xs"
                  variant={errorThreshold === 'low' ? 'solid' : 'outline'}
                  colorScheme="green"
                  borderRadius="md"
                  fontSize="3xs"
                  onClick={() => { setErrorThreshold('low'); setPage(1); }}
                >
                  🟢 &lt; 40%
                </Button>
              </>
            )}
          </HStack>

          {/* Search & Sort Dropdowns */}
          <HStack spacing={2}>
            <InputGroup size="xs" maxW="180px">
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="#94a3b8" />
              </InputLeftElement>
              <Input
                placeholder="Search questions..."
                borderRadius="md"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                fontSize="xs"
              />
            </InputGroup>

            <Select
              size="xs"
              borderRadius="md"
              maxW="140px"
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
              fontSize="2xs"
            >
              <option value="errorDesc">Highest Error %</option>
              <option value="errorAsc">Lowest Error %</option>
              <option value="wrongDesc">Most Missed</option>
            </Select>
          </HStack>
        </Flex>

        {/* TAB 1: DIAGNOSTICS COMPACT LIST (Page-Controlled, No Endless Scrolling) */}
        {activeTab === 'diagnostics' && (
          <Box>
            {paginatedQuestions.length > 0 ? (
              <VStack spacing={2} align="stretch">
                {paginatedQuestions.map((q, idx) => {
                  const globalIdx = (page - 1) * pageSize + idx;
                  const isHigh = q.errorRate >= 70;
                  const isMed = q.errorRate >= 40 && q.errorRate < 70;
                  const rankColor = isHigh ? 'red' : isMed ? 'orange' : 'teal';

                  return (
                    <Box
                      key={q.questionId || globalIdx}
                      px={3}
                      py={2}
                      borderRadius="lg"
                      bg={useColorModeValue('gray.50', 'whiteAlpha.50')}
                      border="1px solid"
                      borderColor={borderColor}
                      _hover={{
                        borderColor: '#0ea5e9',
                        bg: rowHoverBg
                      }}
                      transition="all 0.15s ease"
                      cursor="pointer"
                      onClick={() => handleInspect(q)}
                    >
                      <Flex justify="space-between" align="center" gap={3}>
                        {/* Left: Rank, Question Text */}
                        <HStack spacing={2} flex={1} overflow="hidden">
                          <Badge
                            colorScheme={rankColor}
                            variant="solid"
                            borderRadius="md"
                            px={1.5}
                            py={0.2}
                            fontSize="3xs"
                            fontWeight="800"
                          >
                            #{globalIdx + 1}
                          </Badge>

                          {q.chapterName && (
                            <Badge
                              variant="outline"
                              colorScheme="cyan"
                              fontSize="3xs"
                              px={1.5}
                              borderRadius="md"
                              maxW="110px"
                              isTruncated
                            >
                              {q.chapterName}
                            </Badge>
                          )}

                          <Text
                            fontSize="xs"
                            fontWeight="700"
                            isTruncated
                            title={q.questionText}
                          >
                            {q.questionText}
                          </Text>
                        </HStack>

                        {/* Right: Refined Accuracy / Error Capsule Badge & Action */}
                        <HStack spacing={2.5} flexShrink={0}>
                          <Badge
                            colorScheme={rankColor}
                            variant={isHigh ? 'solid' : 'subtle'}
                            borderRadius="full"
                            px={2.5}
                            py={0.5}
                            fontSize="3xs"
                            fontWeight="800"
                          >
                            {q.errorRate}% Error &bull; {q.wrongAttempts}/{q.totalAttempts} missed
                          </Badge>

                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="cyan"
                            borderRadius="lg"
                            fontSize="3xs"
                            h="24px"
                            px={2}
                            leftIcon={<FontAwesomeIcon icon={faEye} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInspect(q);
                            }}
                          >
                            Inspect
                          </Button>
                        </HStack>
                      </Flex>
                    </Box>
                  );
                })}
              </VStack>
            ) : (
              <Flex justify="center" align="center" py={8} color={mutedText} fontSize="xs">
                No questions match your filter criteria.
              </Flex>
            )}
          </Box>
        )}

        {/* TAB 2: SURVEY POLLS & RESPONSE DISTRIBUTION GRAPHS */}
        {activeTab === 'surveys' && (
          <Box>
            {paginatedQuestions.length > 0 ? (
              <VStack spacing={3} align="stretch">
                {paginatedQuestions.map((q, idx) => (
                  <Box
                    key={q.questionId || idx}
                    p={3.5}
                    borderRadius="xl"
                    bg={useColorModeValue('gray.50', 'whiteAlpha.50')}
                    border="1px solid"
                    borderColor="rgba(14, 165, 233, 0.25)"
                  >
                    <Flex justify="space-between" align="start" mb={2}>
                      <HStack spacing={2} align="center">
                        <Badge colorScheme="purple" variant="solid" borderRadius="md" px={2} fontSize="3xs">
                          📊 POLL #{idx + 1}
                        </Badge>
                        {q.chapterName && (
                          <Badge variant="subtle" colorScheme="cyan" fontSize="3xs" px={2} borderRadius="full">
                            {q.chapterName}
                          </Badge>
                        )}
                      </HStack>
                      <Badge colorScheme="cyan" variant="outline" fontSize="3xs" px={2} borderRadius="full">
                        {q.totalAttempts} Respondent{q.totalAttempts > 1 ? 's' : ''}
                      </Badge>
                    </Flex>

                    {/* Survey Prompt */}
                    <Text fontSize="xs" fontWeight="800" mb={3}>
                      {q.questionText}
                    </Text>

                    {/* Survey Response Distribution Graph */}
                    <VStack spacing={2} align="stretch">
                      {q.pollDistribution && q.pollDistribution.length > 0 ? (
                        q.pollDistribution.map((opt, optIdx) => (
                          <Box key={optIdx}>
                            <Flex justify="space-between" fontSize="2xs" mb={0.5}>
                              <HStack spacing={1.5}>
                                <Text fontWeight="700" color={opt.isTopChoice ? '#10b981' : undefined}>
                                  {opt.option}
                                </Text>
                                {opt.isTopChoice && (
                                  <Badge colorScheme="green" variant="solid" fontSize="4xs" px={1} borderRadius="sm">
                                    TOP CHOICE
                                  </Badge>
                                )}
                              </HStack>
                              <Text fontWeight="800" color={opt.isTopChoice ? '#10b981' : mutedText}>
                                {opt.votes} votes ({opt.percentage}%)
                              </Text>
                            </Flex>
                            <Box h="8px" bg={useColorModeValue('blackAlpha.100', 'whiteAlpha.100')} borderRadius="full" overflow="hidden">
                              <Box
                                h="100%"
                                w={`${opt.percentage}%`}
                                bg={opt.isTopChoice ? 'linear-gradient(90deg, #0ea5e9 0%, #10b981 100%)' : 'rgba(148, 163, 184, 0.4)'}
                                borderRadius="full"
                              />
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Text fontSize="xs" color={mutedText} fontStyle="italic">
                          No poll responses recorded for this question yet.
                        </Text>
                      )}
                    </VStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Flex justify="center" align="center" py={10} flexDir="column" color={mutedText}>
                <Box p={3} borderRadius="full" bg={useColorModeValue('gray.100', 'whiteAlpha.100')} mb={2}>
                  <FontAwesomeIcon icon={faPoll} size="2x" color="#0ea5e9" />
                </Box>
                <Text fontSize="xs" fontWeight="700">No Survey Poll Questions Found</Text>
                <Text fontSize="3xs" mt={0.5}>
                  Questions designated as Survey / Poll format will appear here with live response distribution graphs.
                </Text>
              </Flex>
            )}
          </Box>
        )}

        {/* TAB 3: QUESTIONS & ANSWERS ROSTER */}
        {activeTab === 'roster' && (
          <Box>
            {paginatedQuestions.length > 0 ? (
              <VStack spacing={3} align="stretch">
                {paginatedQuestions.map((q, idx) => (
                  <Box
                    key={q.questionId || idx}
                    p={3.5}
                    borderRadius="xl"
                    bg={useColorModeValue('gray.50', 'whiteAlpha.50')}
                    border="1px solid"
                    borderColor={q.errorRate >= 70 ? 'rgba(239, 68, 68, 0.3)' : borderColor}
                  >
                    <Flex justify="space-between" align="center" mb={2}>
                      <HStack spacing={2}>
                        <Badge colorScheme={q.errorRate >= 70 ? 'red' : 'teal'} borderRadius="md" px={2} fontSize="3xs">
                          #{idx + 1}
                        </Badge>
                        {q.chapterName && (
                          <Badge variant="subtle" colorScheme="cyan" fontSize="3xs" px={2} borderRadius="full">
                            Section: {q.chapterName}
                          </Badge>
                        )}
                      </HStack>
                      <Badge colorScheme={q.errorRate >= 70 ? 'red' : 'teal'} fontSize="3xs" px={2} borderRadius="full">
                        {q.errorRate}% Missed ({q.wrongAttempts}/{q.totalAttempts})
                      </Badge>
                    </Flex>

                    <Text fontSize="xs" fontWeight="700" mb={2.5}>
                      {q.questionText}
                    </Text>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2.5}>
                      <Box p={2.5} borderRadius="lg" bg="rgba(16, 185, 129, 0.1)" border="1px solid rgba(16, 185, 129, 0.3)">
                        <Flex align="center" gap={1} mb={0.5}>
                          <FontAwesomeIcon icon={faCheck} color="#10b981" fontSize="10px" />
                          <Text fontSize="4xs" fontWeight="800" textTransform="uppercase" color="#10b981">
                            Correct Answer
                          </Text>
                        </Flex>
                        <Text fontSize="2xs" fontWeight="700" color="#10b981">
                          {q.correctAnswer || 'Survey / Ungraded'}
                        </Text>
                      </Box>

                      <Box p={2.5} borderRadius="lg" bg="rgba(239, 68, 68, 0.08)" border="1px solid rgba(239, 68, 68, 0.25)">
                        <Flex align="center" gap={1} mb={0.5}>
                          <FontAwesomeIcon icon={faTimes} color="#ef4444" fontSize="10px" />
                          <Text fontSize="4xs" fontWeight="800" textTransform="uppercase" color="red.400">
                            Common Misconception
                          </Text>
                        </Flex>
                        <Text fontSize="2xs" color={mutedText}>
                          {q.commonWrongAnswer || 'No wrong selections'}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Flex justify="center" align="center" py={8} color={mutedText} fontSize="xs">
                No questions found.
              </Flex>
            )}
          </Box>
        )}

        {/* Space-Saving Pagination Bar (Only renders if > 1 page) */}
        {totalPages > 1 && (
          <Flex justify="space-between" align="center" pt={3} mt={3} borderTop="1px solid" borderColor={borderColor}>
            <Text fontSize="3xs" color={mutedText}>
              Showing {(page - 1) * pageSize + 1} &ndash; {Math.min(page * pageSize, filteredQuestions.length)} of {filteredQuestions.length} questions
            </Text>
            <HStack spacing={1.5}>
              <IconButton
                aria-label="Previous Page"
                icon={<FontAwesomeIcon icon={faChevronLeft} fontSize="10px" />}
                size="xs"
                variant="outline"
                isDisabled={page === 1}
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              />
              <Text fontSize="3xs" fontWeight="700" px={2}>
                Page {page} of {totalPages}
              </Text>
              <IconButton
                aria-label="Next Page"
                icon={<FontAwesomeIcon icon={faChevronRight} fontSize="10px" />}
                size="xs"
                variant="outline"
                isDisabled={page === totalPages}
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
              />
            </HStack>
          </Flex>
        )}

        {/* QUESTION INSPECTION MODAL */}
        <Modal isOpen={isInspectOpen} onClose={onInspectClose} size="lg" isCentered>
          <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
          <ModalContent borderRadius="2xl" border="1px solid" borderColor={borderColor} bg={cardBg}>
            <ModalHeader pb={1}>
              <HStack spacing={2}>
                <Badge
                  colorScheme={inspectQuestion && inspectQuestion.errorRate >= 70 ? 'red' : 'teal'}
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  fontSize="2xs"
                >
                  {inspectQuestion?.errorRate}% Error Rate
                </Badge>
                {inspectQuestion?.chapterName && (
                  <Badge variant="outline" colorScheme="cyan" fontSize="3xs">
                    {inspectQuestion.chapterName}
                  </Badge>
                )}
                {inspectQuestion?.quizTitle && (
                  <Badge variant="subtle" colorScheme="purple" fontSize="3xs">
                    {inspectQuestion.quizTitle}
                  </Badge>
                )}
              </HStack>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody py={4}>
              {inspectQuestion && (
                <VStack spacing={3.5} align="stretch">
                  <Box>
                    <Text fontSize="3xs" fontWeight="800" textTransform="uppercase" color={mutedText} mb={1}>
                      Question Text
                    </Text>
                    <Text fontSize="sm" fontWeight="800">
                      {inspectQuestion.questionText}
                    </Text>
                  </Box>

                  {/* Stats Bar */}
                  <Flex justify="space-between" p={2.5} borderRadius="xl" bg={useColorModeValue('gray.50', 'whiteAlpha.100')} fontSize="xs">
                    <Box>
                      <Text color={mutedText} fontSize="3xs">Total Submissions</Text>
                      <Text fontWeight="800">{inspectQuestion.totalAttempts}</Text>
                    </Box>
                    <Box>
                      <Text color={mutedText} fontSize="3xs">Incorrect Choices</Text>
                      <Text fontWeight="800" color="red.400">{inspectQuestion.wrongAttempts}</Text>
                    </Box>
                    <Box>
                      <Text color={mutedText} fontSize="3xs">Accuracy Rate</Text>
                      <Text fontWeight="800" color="#10b981">{100 - inspectQuestion.errorRate}%</Text>
                    </Box>
                  </Flex>

                  {/* Correct Answer */}
                  <Box
                    p={3}
                    borderRadius="xl"
                    bg={useColorModeValue('green.50', 'rgba(16, 185, 129, 0.12)')}
                    border="1px solid rgba(16, 185, 129, 0.35)"
                  >
                    <Flex align="center" gap={1.5} mb={0.5}>
                      <FontAwesomeIcon icon={faCheck} color="#10b981" />
                      <Text fontSize="3xs" fontWeight="800" textTransform="uppercase" color="#10b981">
                        Correct Answer
                      </Text>
                    </Flex>
                    <Text fontSize="xs" fontWeight="700" color="#10b981">
                      {inspectQuestion.correctAnswer || 'Survey / Ungraded'}
                    </Text>
                  </Box>

                  {/* Common Misconceptions or Poll Distribution */}
                  <Box
                    p={3}
                    borderRadius="xl"
                    bg={useColorModeValue('red.50', 'rgba(239, 68, 68, 0.08)')}
                    border="1px solid rgba(239, 68, 68, 0.3)"
                  >
                    <Flex align="center" gap={1.5} mb={1}>
                      <FontAwesomeIcon icon={faTimes} color="#ef4444" />
                      <Text fontSize="3xs" fontWeight="800" textTransform="uppercase" color="red.400">
                        Student Mistakes & Misconceptions
                      </Text>
                    </Flex>
                    {inspectQuestion.allWrongChoices && inspectQuestion.allWrongChoices.length > 0 ? (
                      <VStack align="stretch" spacing={1.5}>
                        {inspectQuestion.allWrongChoices.map((w, wIdx) => (
                          <Flex key={wIdx} justify="space-between" align="center" fontSize="xs">
                            <Text fontWeight="600" isTruncated maxW="280px">{w.answer}</Text>
                            <Badge colorScheme="red" borderRadius="md" px={2} fontSize="3xs">
                              {w.count} students ({w.percentage}%)
                            </Badge>
                          </Flex>
                        ))}
                      </VStack>
                    ) : (
                      <Text fontSize="xs" color={mutedText}>
                        {inspectQuestion.commonWrongAnswer || 'No wrong answers on record'}
                      </Text>
                    )}
                  </Box>
                </VStack>
              )}
            </ModalBody>

            <ModalFooter borderTop="1px solid" borderColor={borderColor} py={2.5}>
              <Button size="sm" colorScheme="cyan" borderRadius="xl" onClick={onInspectClose}>
                Done
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default QuestionDifficultyDeck;
